import { useEffect, useMemo } from 'react'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { useReadiness } from '@/lib/readiness/useReadiness'
import { useReadinessStore, discoverySummaryOf } from '@/stores/readinessStore'
import { allObjectives, levelName, tierForScore, tiersOf } from '@/lib/indexes/registry'
import { computeIndexScore, computeScore, liveLevel, liveScore, scoreBand, scoreToPct } from '@/lib/readiness/score'
import { assertScale, fmtScore, isPreliminary } from '@/lib/readiness/scale'
import { levelFromNum, levelNum, transitionFor, type LevelCode, type Snapshot } from '@/api/types/readiness'
import { ScoreRing, Icon } from '@/components/readiness/primitives'
import { BackLink } from '@/components/readiness/shell'
import { BandWord } from '@/components/readiness/ScoreTooltip'
import { formatDate } from '@/components/common/format'
import { useAssessmentEvidence, useAssessmentHeatmap, evidenceFromItem, levelCode } from '@/api/clients/assessmentsApi'

// Readiness report (PDF, I1 Note 9, I2 §10): server-rendered later; for now a
// print-ready page served from the same data as the Assessments and Objective
// pages. The print stylesheet (src/index.css) forces the light theme, fits two
// to three objective cards per A4 page and hides the app chrome. Domain
// colours come from the --rd-domain-* tokens so they follow the print theme.
//
// Iteration 5 §5: `/readiness/report/live` is the Dashboard's **Summary
// report**: it renders the live state at export time (score, band, levels
// including suggested levels, Discovery figures), never a saved assessment.
// Saved-assessment reports keep their view and header.
export default function Report() {
  const { snapshotId = '' } = useParams()
  const [params] = useSearchParams()
  const ctx = useReadiness()
  const { index, scope, orgName, orgId, indexId } = ctx
  const scale = assertScale(index)
  const live = snapshotId === 'live'
  const items = useMemo(() => allObjectives(index), [index])
  const track = useReadinessStore((s) => s.track)
  const now = useMemo(() => new Date(), [])
  // The live report reads what the Dashboard reads: the readiness service's
  // assessment when it serves one, the in-browser engine otherwise.
  const remote = useAssessmentHeatmap(orgId, indexId)
  const served = live && remote.status === 'ready' && remote.heatmap ? remote.heatmap : null
  const codes = useMemo(() => items.map(({ objective }) => objective.code), [items])
  const servedEvidence = useAssessmentEvidence(orgId, served?.assessment.id ?? null, served ? codes : [])
  const liveSnapshot = useMemo<Snapshot>(() => {
    const lastSync = scope.sync_runs.find((r) => r.status === 'succeeded')
    const snapItems = items.map(({ objective }) => {
      const cell = served ? remote.cells?.get(objective.code) : undefined
      if (cell) {
        // The level set in the assessment; a suggestion nobody has accepted is not reported.
        const basis = served!.assessment.source === 'csa_report' ? ('csa_report' as const) : ('internal' as const)
        return { snapshot_id: 'live', objective_code: objective.code, level: levelCode(cell.level) ?? 'L0', status: cell.level == null ? ('unset' as const) : ('declared' as const), evidence_ids: (servedEvidence.get(objective.code) ?? []).map((e) => e.id), basis, score: null, previous_level: levelCode(cell.previous_level) }
      }
      const st = scope.states[objective.code]
      return { snapshot_id: 'live', objective_code: objective.code, level: liveLevel(st), status: st?.status ?? 'unset', evidence_ids: scope.evidence.filter((e) => e.objective_code === objective.code && !e.archived_at).map((e) => e.id), basis: st?.basis ?? 'internal', score: scale === 'score_0_10' ? liveScore(st) : null, previous_level: null }
    })
    const score = scale === 'levels' ? computeScore(snapItems.map((i) => i.level)) : (computeIndexScore(index, (c) => snapItems.find((i) => i.objective_code === c)?.score ?? null).overall ?? 0)
    return { id: 'live', org_id: orgId, index_id: indexId, index_version: index.version, name: 'Summary Report', note: null, score, created_by: 'live', created_at: now.toISOString(), as_of: now.toISOString(), baseline: false, scheduled: false, items: snapItems, discovery_summary: discoverySummaryOf(scope.settings.discovery_facts, lastSync?.finished_at ?? lastSync?.started_at ?? null), projection: null }
  }, [items, scope, scale, index, orgId, indexId, now, served, remote.cells, servedEvidence])
  const snapshot = live ? liveSnapshot : scope.snapshots.find((s) => s.id === snapshotId)

  useEffect(() => {
    document.body.classList.add('rd-print')
    const t = params.get('print') === '1' ? window.setTimeout(() => window.print(), 600) : null
    return () => {
      document.body.classList.remove('rd-print')
      if (t) window.clearTimeout(t)
    }
  }, [params])
  useEffect(() => {
    if (live) track(orgId, indexId, { name: 'readiness.summary_report_exported' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live])

  if (!snapshot) return <Navigate to={`/readiness/progress?index=${indexId}`} replace />
  const band = scale === 'levels' ? scoreBand(snapshot.score) : scoreBand(scoreToPct(snapshot.score))
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const liveMeta = `Live · as of ${formatDate(now.toISOString())}, ${now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} ${tz} · ${index.name} ${index.version}`
  const itemOf = (code: string) => snapshot.items.find((i) => i.objective_code === code)
  const levelOf = (code: string) => itemOf(code)?.level ?? 'L0'
  // An objective with no level reads Not set, never L0.
  const unsetOf = (code: string) => itemOf(code)?.status === 'unset' && !!served
  const levelText = (code: string) => (unsetOf(code) ? 'Not set' : `${levelOf(code)} · ${levelName(index, levelOf(code))}`)
  const evidenceOf = (code: string) => {
    if (served) return (servedEvidence.get(code) ?? []).map((e) => evidenceFromItem(e, orgId, code))
    const ids = new Set(itemOf(code)?.evidence_ids ?? [])
    return scope.evidence.filter((e) => ids.has(e.id))
  }
  const previous = (() => {
    const i = scope.snapshots.findIndex((s) => s.id === snapshot.id)
    return i > 0 ? scope.snapshots[i - 1] : null
  })()
  const prevLevelOf = (code: string): LevelCode | null => itemOf(code)?.previous_level ?? (previous ? (previous.items.find((i) => i.objective_code === code)?.level ?? 'L0') : null)
  const domainTone = (code: string) => `var(--rd-domain-${code.toLowerCase()})`
  const preliminary = isPreliminary(index) && index.preliminary ? `Preliminary alignment view · ${index.preliminary.notice}` : null
  const footerText = `Generated by Quantizant Readiness · ${index.name} ${index.version} · ${formatDate(new Date().toISOString())}`
  const coverLine = live ? liveMeta : `${snapshot.name} · as of ${formatDate(snapshot.as_of ?? snapshot.created_at)} · saved by ${snapshot.created_by} · ${index.name} ${index.version}`
  const fmt = (v: number) => (scale === 'levels' ? String(v) : v.toFixed(1))

  return (
    <div id="rd-report" className="rd -m-6 min-h-screen p-8" style={{ background: 'var(--rd-bg-page)' }} data-testid="readiness-report">
      {/* Back link follows the origin (I5 §4): `← Dashboard` for the summary report, `← Progress` for a saved assessment. */}
      <div className="rd-no-print mb-4 flex flex-wrap items-center gap-2" data-testid="back-link">
        <BackLink />
        <button type="button" className="rd-btn rd-btn-primary ml-auto" onClick={() => window.print()}><Icon name="download" size={14} /> Save as PDF</button>
      </div>

      {/* Cover */}
      <section className="rd-card p-8">
        <p className="rd-label">{index.publisher} · {index.long_name} {index.version}</p>
        {/* Report covers are title case: they are document titles (I6 §11.2). Everything inside stays sentence case. */}
        <h1 className="mt-2 text-[28px] font-bold" data-testid="report-title">{live ? 'Summary Report' : 'Readiness Report'} · {orgName || 'Organisation'}</h1>
        <p className="mt-1 text-[14px]" style={{ color: 'var(--rd-text-muted)' }} data-testid="report-cover-line">{coverLine}{snapshot.baseline ? ' · baseline' : ''}</p>
        {live && <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-secondary)' }} data-testid="report-live-note">Live snapshot, not a saved assessment. <Link to={`/readiness?index=${indexId}&save=1`} className="rd-link rd-no-print">Save an assessment</Link><span className="hidden print:inline">Save an assessment</span> to keep an auditable record.</p>}
        {preliminary && <p className="mt-2 text-[11px]" style={{ color: 'var(--rd-status-warn)' }}>{preliminary}</p>}
        <div className="mt-6 flex items-center gap-6">
          <ScoreRing score={scale === 'levels' ? snapshot.score : scoreToPct(snapshot.score)} color={band.token} size={128} stroke={12}>
            <span className="text-[34px] font-bold">{fmt(snapshot.score)}</span>
          </ScoreRing>
          <div>
            <p className="text-[20px] font-semibold" style={{ color: band.token }}>
              {scale === 'levels' ? (
                <BandWord band={band} testId="report-band" />
              ) : `${fmt(snapshot.score)} of 10${isPreliminary(index) ? ' · indicative' : ''}`}
            </p>
            <p className="text-[13px]" style={{ color: 'var(--rd-text-muted)' }}>
 {scale === 'levels' ? `Mean level across ${items.length} objectives, as % of L3${served ? '; objectives not yet set count as L0' : live ? ', including suggested levels not yet confirmed' : ''}. Quantizant's summary indicator; the levels below are the CSA measure.` : `Mean of the ${index.domains.length} dimension scores, each the mean of its scored sub-indices. ${index.benchmarks ? `Sector mean ${index.benchmarks.overall.toFixed(1)} (${index.benchmarks.source}).` : ''}`}
            </p>
            {snapshot.discovery_summary && (
              <p className="mt-1 text-[12px]" style={{ color: 'var(--rd-text-muted)' }}>
                Discovery {live ? 'now' : 'at save time'}: {snapshot.discovery_summary.assets_total.toLocaleString()} assets · {snapshot.discovery_summary.assets_not_migrated.toLocaleString()} not migrated · {snapshot.discovery_summary.sources} sources{snapshot.discovery_summary.last_scan_at ? ` · last scan ${formatDate(snapshot.discovery_summary.last_scan_at)}` : ''}
              </p>
            )}
          </div>
        </div>
        <h2 className="rd-label mt-8">{scale === 'levels' ? 'Domain summary' : 'Dimension summary'}</h2>
        <table className="mt-2 w-full text-[13px]">
          <tbody>
            {[...index.domains].sort((a, b) => a.order - b.order).map((d) => (
              <tr key={d.code} style={{ borderBottom: '1px solid var(--rd-border-subtle)' }}>
                <td className="py-2 font-medium" style={{ color: domainTone(d.code), width: 180 }}>{d.name}</td>
                {d.objectives.map((o) => (
                  <td key={o.code} className="py-2 pr-2"><span className="rd-mono" style={{ color: 'var(--rd-text-muted)' }}>{o.code}</span> {scale === 'levels' ? levelText(o.code) : fmtScore(itemOf(o.code)?.score ?? null)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Objective cards: two to three per page (I2 §10.2) */}
      {items.map(({ domain, objective }) => {
        const l = levelOf(objective.code)
        const it = itemOf(objective.code)
        const prev = prevLevelOf(objective.code)
        const next = l === 'L3' ? null : levelFromNum(levelNum(l) + 1)
        const ev = evidenceOf(objective.code)
        const color = domainTone(domain.code)
        const tier = scale === 'score_0_10' ? tierForScore(index, objective.code, it?.score ?? null) : null
        const steps = scale === 'levels' ? (objective.recommendations[transitionFor(l)] ?? []) : (objective.actions ?? []).map((a) => `${a.text} (${a.source_page})`)
        return (
          <section key={objective.code} className="rd-card objective-card mt-4 p-8">
            <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color }}>{domain.name}</p>
            <h2 className="mt-1 text-[18px] font-semibold"><span className="rd-mono" style={{ color: 'var(--rd-text-muted)' }}>{objective.code}</span> · {objective.title}</h2>
            <p className="mt-2 flex flex-wrap items-baseline gap-x-3 text-[16px] font-bold" style={{ color }}>
              {scale === 'levels' ? (
                <>
                  <span>{levelText(objective.code)}</span>
                  {prev != null && prev !== l && !unsetOf(objective.code) && <span className="text-[12px] font-medium" style={{ color: levelNum(l) > levelNum(prev) ? 'var(--rd-status-ok-text)' : 'var(--rd-status-warn)' }}>{prev} → {l}{levelNum(l) < levelNum(prev) ? ' ▼' : ''}</span>}
                  <span className="text-[11px] font-medium" style={{ color: 'var(--rd-text-muted)' }}>Basis: {it?.basis === 'csa_report' ? 'CSA QRI report attached' : 'Internal judgement'}</span>
                </>
              ) : (
                <>
                  <span>{fmtScore(it?.score ?? null)}{tier ? ` · tier ${tier.n} of ${tiersOf(index, objective.code).length}` : ' · not scored'}</span>
                  {previous && (() => {
                    const ps = previous.items.find((i) => i.objective_code === objective.code)?.score ?? null
                    return ps != null && ps !== (it?.score ?? null) ? <span className="text-[12px] font-medium" style={{ color: (it?.score ?? 0) > ps ? 'var(--rd-status-ok-text)' : 'var(--rd-status-warn)' }}>{fmtScore(ps)} → {fmtScore(it?.score ?? null)}</span> : null
                  })()}
                </>
              )}
            </p>
            <p className="mt-1 text-[12px]" style={{ color: 'var(--rd-text-secondary)' }}>{scale === 'levels' ? objective.levels[l] : tier?.statement ?? objective.description}</p>
            <h3 className="rd-label mt-4">Evidence</h3>
            {ev.length === 0 ? (
              <p className="mt-1 text-[12px]" style={{ color: 'var(--rd-text-muted)' }}>{live ? 'No evidence yet.' : 'No evidence recorded in this assessment.'}</p>
            ) : (
              <table className="mt-1 w-full text-[12px]">
                <tbody>
                  {ev.map((e) => (
                    <tr key={e.id} style={{ borderBottom: '1px solid var(--rd-border-subtle)' }}>
                      <td className="py-1 pr-3 capitalize" style={{ color: 'var(--rd-text-muted)', width: 90 }}>{e.source}</td>
                      <td className="py-1 pr-3">{e.title}{e.summary ? <span style={{ color: 'var(--rd-text-muted)' }}> · {e.summary}</span> : null}</td>
                      <td className="rd-mono py-1" style={{ width: 100, color: 'var(--rd-text-muted)' }}>{formatDate(e.occurred_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <h3 className="rd-label mt-4">{scale === 'levels' ? (next ? `Next steps · to reach ${next}` : 'Sustain') : 'Next steps'}</h3>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-[12px]">
              {steps.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </section>
        )
      })}

      {/* Evidence index: the only forced page break (I2 §10.2) */}
      <section className="rd-card rd-page-break mt-4 p-8">
        <h2 className="text-[20px] font-semibold">Evidence index</h2>
        <table className="mt-3 w-full text-[12px]">
          <thead>
            <tr style={{ color: 'var(--rd-text-muted)', borderBottom: '1px solid var(--rd-border-subtle)' }}>
              <th className="py-1.5 text-left font-medium">{scale === 'levels' ? 'Objective' : 'Sub-index'}</th><th className="py-1.5 text-left font-medium">Source</th><th className="py-1.5 text-left font-medium">Title</th><th className="py-1.5 text-left font-medium">Date</th>
            </tr>
          </thead>
          <tbody>
            {snapshot.items.flatMap((i) => evidenceOf(i.objective_code).map((e) => (
              <tr key={e.id} style={{ borderBottom: '1px solid var(--rd-border-subtle)' }}>
                <td className="rd-mono py-1.5 pr-3">{i.objective_code}</td><td className="py-1.5 pr-3 capitalize">{e.source}</td><td className="py-1.5 pr-3">{e.title}</td><td className="rd-mono py-1.5">{formatDate(e.occurred_at)}</td>
              </tr>
            )))}
          </tbody>
        </table>
        <p className="mt-6 text-[11px]" style={{ color: 'var(--rd-text-faint)' }}>{footerText}{preliminary ? ` · ${preliminary}` : ''}</p>
      </section>

      {/* Repeating page footer (print only) */}
      <div className="rd-print-footer" aria-hidden="true">
        <span>{footerText}{isPreliminary(index) ? ' · Preliminary alignment view · indicative, not a regulator result' : ''}</span>
        <span className="rd-page-number" />
      </div>
    </div>
  )
}
